import { useState } from "react";
import styled from "styled-components";

import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";

const ENDPOINT = "/Painel/API/Home/generate_contratos.php";

const TERMOS = [
    { key: "titulo_exp", label: "CONTRATO DE EXPERIÊNCIA" },
    { key: "prorrog_exp", label: "TERMO DE PRORROGAÇÃO DE EXPERIÊNCIA" },
    { key: "compens_hrs", label: "ACORDO DE COMPENSAÇÃO DE HORAS" },
    { key: "cod_conduta", label: "CIÊNCIA DO CÓDIGO DE CONDUTA" },
    { key: "consent_img", label: "CONSENTIMENTO PARA USO DE IMAGEM" },
    { key: "solicit_vt", label: "SOLICITAÇÃO DE VALE-TRANSPORTE" },
    { key: "depend_imp", label: "DECLARAÇÃO DE DEPENDENTES (IR)" },
];

const Title = styled.h3`
    margin: 0 0 4px 0;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
`;

const Subtitle = styled.p`
    margin: 0 0 16px 0;
    padding: 0 0 12px 0;
    border-bottom: 1px solid #e5e7eb;
    font-size: 13px;
    color: #6b7280;
`;

const ListaTermos = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 50dvh;
    overflow-y: auto;
`;

const TermoItem = styled.label`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid ${({ $selected }) => ($selected ? "#007bff" : "#e5e7eb")};
    background-color: ${({ $selected }) => ($selected ? "#e6f7ff" : "#fff")};
    border-radius: 8px;
    font-size: 13px;
    color: #1f2937;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    &:hover {
        border-color: #007bff;
    }
`;

const SelecionarTodos = styled.button`
    align-self: flex-start;
    margin-bottom: 10px;
    padding: 4px 0;
    background: none;
    border: none;
    color: #007bff;
    font-size: 13px;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

const Rodape = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
    gap: 12px;
`;

const Info = styled.span`
    font-size: 13px;
    color: #4b5563;
`;

const BotaoGerar = styled.button`
    padding: 9px 18px;
    background-color: #007bff;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: #0056b3;
    }

    &:disabled {
        background-color: #9bc4ec;
        cursor: not-allowed;
    }
`;

const lerMensagemDeErroBlob = async (blob) => {
    try {
        const json = JSON.parse(await blob.text());
        return json?.message ?? null;
    } catch {
        return null;
    }
};

const sanitizarNome = (nome) =>
    String(nome ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^A-Za-z0-9_]/g, "")
        .toUpperCase();

const ModalSelecionarTermos = ({ colaboradores = [] }) => {

    const { closeModal } = useModal();
    const { notifySuccess, notifyError, notifyWarning } = useToast();
    const api = useApi();

    const [termosSelecionados, setTermosSelecionados] = useState([]);
    const [gerando, setGerando] = useState(false);
    const [progresso, setProgresso] = useState({ atual: 0, total: 0 });

    const todosSelecionados = termosSelecionados.length === TERMOS.length;

    const toggleTermo = (key) => {
        setTermosSelecionados((prev) =>
            prev.includes(key)
                ? prev.filter((item) => item !== key)
                : [...prev, key]
        );
    };

    const toggleTodos = () => {
        setTermosSelecionados(
            todosSelecionados ? [] : TERMOS.map((termo) => termo.key)
        );
    };

    const gerarContratoColaborador = async (colaborador) => {
        const formData = new FormData();
        formData.append("matricula", colaborador.MATRICULA);
        formData.append("termos", JSON.stringify(termosSelecionados));

        const response = await api.post(ENDPOINT, formData, {
            responseType: "blob",
        });

        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `CONTRATO_${sanitizarNome(colaborador.NOME)}_${colaborador.MATRICULA}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
    };

    const gerar = async () => {
        if (gerando) return;

        if (termosSelecionados.length === 0) {
            notifyWarning("Selecione ao menos um termo para gerar.");
            return;
        }

        if (colaboradores.length === 0) {
            notifyWarning("Nenhum colaborador selecionado.");
            return;
        }

        setGerando(true);
        setProgresso({ atual: 0, total: colaboradores.length });

        const falhas = [];

        for (let i = 0; i < colaboradores.length; i++) {
            const colaborador = colaboradores[i];

            try {
                await gerarContratoColaborador(colaborador);
            } catch (error) {
                let msg = "Erro ao gerar contrato.";
                const data = error?.response?.data;

                if (data instanceof Blob) {
                    const apiMsg = await lerMensagemDeErroBlob(data);
                    if (apiMsg) msg = apiMsg;
                } else if (typeof data?.message === "string") {
                    msg = data.message;
                }

                falhas.push(`${colaborador.MATRICULA}: ${msg}`);
            }

            setProgresso({ atual: i + 1, total: colaboradores.length });
        }

        setGerando(false);

        const sucessos = colaboradores.length - falhas.length;

        if (sucessos > 0) {
            notifySuccess(
                `${sucessos} contrato${sucessos === 1 ? "" : "s"} gerado${sucessos === 1 ? "" : "s"} com sucesso.`
            );
        }

        if (falhas.length > 0) {
            notifyError(`Falha em ${falhas.length}: ${falhas.join(" | ")}`);
            return;
        }

        closeModal();
    };

    const textoBotao = gerando
        ? `GERANDO ${progresso.atual}/${progresso.total}...`
        : "GERAR";

    return (
        <Overlay state changeContent={gerando ? undefined : closeModal}>
            <Modal header close={gerando ? undefined : closeModal} size="md">
                <Title>SELECIONE OS TERMOS</Title>
                <Subtitle>
                    {colaboradores.length} colaborador
                    {colaboradores.length === 1 ? "" : "es"} selecionado
                    {colaboradores.length === 1 ? "" : "s"} — será gerado um PDF por
                    colaborador.
                </Subtitle>

                <SelecionarTodos type="button" onClick={toggleTodos}>
                    {todosSelecionados ? "Limpar seleção" : "Selecionar todos"}
                </SelecionarTodos>

                <ListaTermos>
                    {TERMOS.map((termo) => {
                        const selecionado = termosSelecionados.includes(termo.key);

                        return (
                            <TermoItem key={termo.key} $selected={selecionado}>
                                <input
                                    type="checkbox"
                                    checked={selecionado}
                                    onChange={() => toggleTermo(termo.key)}
                                />
                                {termo.label}
                            </TermoItem>
                        );
                    })}
                </ListaTermos>

                <Rodape>
                    <Info>
                        {termosSelecionados.length} termo
                        {termosSelecionados.length === 1 ? "" : "s"} selecionado
                        {termosSelecionados.length === 1 ? "" : "s"}
                    </Info>
                    <BotaoGerar
                        onClick={gerar}
                        disabled={gerando || termosSelecionados.length === 0}
                    >
                        {textoBotao}
                    </BotaoGerar>
                </Rodape>
            </Modal>
        </Overlay>
    );
};

export default ModalSelecionarTermos;
